import React from 'react';
import { UPLOAD_WORDS, uploadPercent, uploadProgressText } from '../hooks/useUploads';
import s from './UploadProgress.module.css';

// One file on its way up, drawn the same at every upload site on the web
// (#612): the library and Add New Online Resources (#577), New Message's
// Attach Files (#605), and an expense's receipts — attached, or going up
// beside the receipt reader. Both phones draw the same row
// (`UploadProgressRow` on each), from the same core words.
//
//   waiting   → the name and "Waiting", and × to take it off.
//   uploading → the name, the core's amount (`40% · 48 KB of 120 KB`) and a
//               determinate bar — a `<progress>`, so it is a progressbar to a
//               screen reader, with the amount as its value text — and × to
//               cancel.
//   failed    → the name, the sentence (the timeout's, when the no-progress
//               watchdog gave up on it, src/lib/uploadWatch.js), Try again,
//               and × to dismiss.
//
// A tiny upload (a small receipt) may flash through `uploading` too fast to
// read, which is fine: the row is there for the ones that take long enough
// to wonder about.
//
// `cancelLabel` and `dismissLabel` are the words the × is read as while the
// file is going up and once it has failed: Cancel and Dismiss by default,
// Remove on Attach Files, where × takes the file off the message whatever it
// is doing. No × is drawn without its handler.

export function UploadProgress({
  item, onRetry = null, onCancel = null, onDismiss = null,
  cancelLabel = UPLOAD_WORDS.cancel, dismissLabel = UPLOAD_WORDS.dismiss, as: Tag = 'div',
}) {
  const {
    key, name, status, message,
  } = item;
  const going = status === 'uploading';
  const text = going ? uploadProgressText(item) : null;
  const close = status === 'failed' ? onDismiss : onCancel;
  const closeLabel = status === 'failed' ? dismissLabel : cancelLabel;
  return (
    <Tag className={s.upload}>
      <div className={s.head}>
        <span className={s.name}>{name}</span>
        {going ? <span className={s.amount}>{text}</span> : null}
        {status === 'waiting' ? <span className={s.amount}>{UPLOAD_WORDS.waiting}</span> : null}
        {close ? (
          <button
            type="button"
            className={s.close}
            aria-label={`${closeLabel} ${name}`}
            onClick={() => close(key)}
          >
            ×
          </button>
        ) : null}
      </div>
      {going ? (
        <progress
          className={s.bar}
          max={100}
          value={uploadPercent(item)}
          aria-label={`${UPLOAD_WORDS.uploading} ${name}`}
          aria-valuetext={text}
        />
      ) : null}
      {status === 'failed' ? (
        <div className={s.failed}>
          <span className={s.error} role="alert">{message}</span>
          {onRetry ? (
            <button type="button" className={s.retry} onClick={() => onRetry(key)}>{UPLOAD_WORDS.retry}</button>
          ) : null}
        </div>
      ) : null}
    </Tag>
  );
}

// The rows as a list. Nothing is drawn when nothing is on it.
export default function UploadProgressList({
  items, label = 'Uploads', onRetry = null, onCancel = null, onDismiss = null, cancelLabel, dismissLabel,
}) {
  if (!items?.length) return null;
  return (
    <ul className={s.uploads} aria-label={label}>
      {items.map((it) => (
        <UploadProgress
          key={it.key}
          as="li"
          item={it}
          onRetry={onRetry}
          onCancel={onCancel}
          onDismiss={onDismiss}
          cancelLabel={cancelLabel}
          dismissLabel={dismissLabel}
        />
      ))}
    </ul>
  );
}
